// Builds JSON:API-style query strings with bracketed filter paths and operator keys.
// Example output:
// page[size]=6&page[number]=1&include=event.venue,ticketType&filter[fishId]=...&filter[event][startDateTime][gte]=2025-09-09T18:47:00Z

type PageParams = { size?: number; number?: number };

type ComparatorObj = Partial<{
  // numeric/date comparators
  gte: string | number;
  gt: string | number;
  lte: string | number;
  lt: string | number;

  // equality / set ops
  equals: string | number | boolean | null;
  not: string | number | boolean | null;

  // membership
  in: Array<string | number | boolean | null>;
  notIn: Array<string | number | boolean | null>;

  // text ops if you choose to support (API can also do wildcards via string, but leaving here)
  contains: string;
  startsWith: string;
  endsWith: string;
  mode: 'insensitive' | 'default';

  // convenience: range -> maps to [gte] + [lte]
  between: [
    string | number | null | undefined,
    string | number | null | undefined,
  ];
}>;

type FilterValue =
  | string
  | number
  | boolean
  | null
  | Array<string | number | boolean | null>
  | ComparatorObj;

type BuildParams = {
  page?: PageParams;
  include?: string[]; // e.g. ['event.venue','ticketType']
  sort?: string[]; // e.g. ['event.startDateTime','-createdAt']
  filter?: Record<string, FilterValue>; // keys may be dot paths like 'event.startDateTime'
};

const isDef = (v: unknown) => v !== undefined && v !== null;

function appendFilter(
  qp: URLSearchParams,
  fieldPath: string, // supports 'event.startDateTime'
  value: FilterValue,
) {
  // Build base like: filter[event][startDateTime]
  const parts = fieldPath.split('.').filter(Boolean);
  let base = 'filter';
  for (const p of parts) base += `[${p}]`;

  // Primitives
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    qp.append(base, value === null ? 'null' : String(value));
    return;
  }

  // Arrays -> comma-joined (for IN-style)
  if (Array.isArray(value)) {
    qp.append(
      base,
      value.map((x) => (x === null ? 'null' : String(x))).join(','),
    );
    return;
  }

  // Comparator object
  const v = value as ComparatorObj;

  // between -> gte/lte (if present)
  if (v.between) {
    const [min, max] = v.between;
    if (isDef(min)) qp.append(`${base}[gte]`, String(min));
    if (isDef(max)) qp.append(`${base}[lte]`, String(max));
  }

  // Emit other supported keys
  const simpleKeys: (keyof ComparatorObj)[] = [
    'gte',
    'gt',
    'lte',
    'lt',
    'equals',
    'not',
    'contains',
    'startsWith',
    'endsWith',
    'mode',
  ];

  for (const k of simpleKeys) {
    const val = v[k];
    if (isDef(val))
      qp.append(`${base}[${k}]`, val === null ? 'null' : String(val));
  }

  // in / notIn -> comma-joined
  if (v.in && Array.isArray(v.in)) {
    qp.append(
      `${base}[in]`,
      v.in.map((x) => (x === null ? 'null' : String(x))).join(','),
    );
  }
  if (v.notIn && Array.isArray(v.notIn)) {
    qp.append(
      `${base}[notIn]`,
      v.notIn.map((x) => (x === null ? 'null' : String(x))).join(','),
    );
  }
}

export function buildJsonApiQuery(params: BuildParams = {}): string {
  const qp = new URLSearchParams();

  // page
  if (isDef(params.page?.size)) qp.set('page[size]', String(params.page!.size));
  if (isDef(params.page?.number))
    qp.set('page[number]', String(params.page!.number));

  // include
  if (params.include?.length) qp.set('include', params.include.join(','));

  // sort
  if (params.sort?.length) qp.set('sort', params.sort.join(','));

  // filters
  if (params.filter) {
    for (const [field, val] of Object.entries(params.filter)) {
      appendFilter(qp, field, val as FilterValue);
    }
  }

  return qp.toString();
}
